import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TProductInstance } from '@lib/core/products/types';
import { getProductsListData } from '@lib/core/products/utils';
import { selectAppliedAddOns } from '@lib/core/retailers/selectors/retailerLocation';
import { ApiQueries, REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { selectUserCharactersForServiceProductCategory } from '@lib/core/users/selectors/characters';
import {
  selectCurrentGameProgress,
  selectGameDetails,
  selectGameEnrollments,
  selectGamesProgress,
} from '@lib/tools/gamification/selectors';
import {
  apiUrlCreateGameEnrollment,
  apiUrlGameEnrollments,
  apiUrlListAllRetailerGames,
  apiUrlListUserGameEnrollments,
  apiUrlUpdateGameProgress,
} from '@lib/tools/gamification/slices/urls';
import {
  TGame,
  TGameEnrollment,
  TGameProgress,
  TGamesProgress,
  TReward,
  TSurveyGame,
} from '@lib/tools/gamification/types';

interface State {
  gameDetails: TGame | null;
  gameEnrollments: { rewards: TReward[]; totalScore: number } | null;
  gamesProgress: TGamesProgress;
  isGameInitializing: boolean;
  isGameProgressUpdating: boolean;
  products: TProductInstance[];
  usedProducts: TProductInstance[];
  leaderboardConfirmationByGame: Record<string, boolean>;
  surveyGame: TSurveyGame | null;
}

const initialState: State = {
  gameDetails: null,
  gameEnrollments: null,
  gamesProgress: {},
  isGameInitializing: false,
  isGameProgressUpdating: false,
  leaderboardConfirmationByGame: {},
  products: [],
  surveyGame: null,
  usedProducts: [],
};

export const createDefaultGameProgress = (): TGameProgress => ({ levels: {}, usedProductIds: [] });

export const actionInitGamification = createTypedAsyncThunk('actionInitGamification', async (_, { getState }) => {
  const { isFreeFormTastingAddon, isGamifiedSurveyQuestionsAddon } = selectAppliedAddOns(getState());

  // All retailer games
  const { results: allRetailerGames = [] }: { results: TGame[] } = await request({
    url: apiUrlListAllRetailerGames(),
  });

  const gameDetails = allRetailerGames[0] ?? null;
  if (!gameDetails) return null;

  // User enrollments for all games
  const { results: allGameEnrollments = [] }: { results: TGameEnrollment[] } = await request({
    url: apiUrlListUserGameEnrollments(),
  });

  let gameEnrollments = allGameEnrollments.find(game => game.game.slug === gameDetails.slug);

  // If no enrollment exists for this game, create one
  if (!gameEnrollments) {
    await request({
      body: { game: gameDetails.slug },
      method: REQUEST_METHODS.POST,
      url: apiUrlCreateGameEnrollment(),
    });

    gameEnrollments = await request({
      url: apiUrlGameEnrollments(gameDetails.slug),
    });
  }

  const state = getState();
  const category = selectServiceProductCategory(state);
  const characterIds = (selectUserCharactersForServiceProductCategory(state) ?? [])
    .map(character => character?.characterId)
    .reverse();
  const usedProductIds = selectGamesProgress(state)?.[gameDetails.slug]?.usedProductIds ?? [];

  // Required number of products for each game step
  const requiredProductsCount =
    (gameDetails?.levels?.filter(l => !l.isOnboarding).reduce((sum, l) => sum + (l.steps?.length ?? 0), 0) ?? 0) + 1;
  const collected: TProductInstance[] = [];

  const loadProductsByCharacterIndex = async (index = 0): Promise<TProductInstance[]> => {
    if (collected.length >= requiredProductsCount || index >= characterIds.length) {
      return collected.slice(0, requiredProductsCount);
    }

    const params = {
      [ApiQueries.productCategory]: category,
      ...(characterIds[index] ? { [ApiQueries.character]: characterIds[index] } : {}),
    };

    const { results = [] } = await getProductsListData(params);
    const fresh = results.filter(p => !usedProductIds.includes(p.product.identifier));
    collected.push(...fresh);

    return loadProductsByCharacterIndex(index + 1);
  };

  // Game products
  // For isFreeFormTastingAddon, the user selects products manually
  const products = isFreeFormTastingAddon || isGamifiedSurveyQuestionsAddon ? [] : await loadProductsByCharacterIndex();
  // Used game products
  const usedProducts =
    usedProductIds.length > 0
      ? ((
          await getProductsListData({
            [ApiQueries.productIdentifier]: usedProductIds.map(id => `%${id}`).join(''),
          })
        ).results ?? [])
      : [];

  return {
    gameDetails,
    gameEnrollments: {
      rewards: gameEnrollments.rewards ?? [],
      totalScore: gameEnrollments.totalScore ?? 0,
    },
    isFreeFormTastingAddon,
    isGamifiedSurveyQuestionsAddon,
    products,
    usedProducts,
  };
});

export const actionUpdateGamificationProgress = createTypedAsyncThunk(
  'actionUpdateGamificationProgress',
  async (
    payload: {
      activeLevelSlug: string;
      activeLevelStepIndex: number | null;
      productInstance?: TProductInstance;
      activeLevelStepScore: number;
      isFreeFormTastingAddon: boolean;
      isGamifiedSurveyQuestionsAddon: boolean;
    },
    { getState },
  ) => {
    const state = getState();
    const currentGameProgress = selectCurrentGameProgress(state);
    const currentGameEnrollments = selectGameEnrollments(state);
    const gameDetails = selectGameDetails(state);

    const {
      activeLevelSlug,
      activeLevelStepIndex,
      productInstance,
      activeLevelStepScore,
      isFreeFormTastingAddon,
      isGamifiedSurveyQuestionsAddon,
    } = payload;

    const levelProgress = currentGameProgress?.levels?.[activeLevelSlug];
    const activeLevelOrder = levelProgress?.levelDetails?.order;
    const updatedLevelTotal = levelProgress?.levelTotalScore + activeLevelStepScore;
    const updatedGameTotal = currentGameEnrollments?.totalScore + activeLevelStepScore;
    const levelScoreTarget = levelProgress?.levelDetails.score;
    // For isFreeFormTastingAddon or isGamifiedSurveyQuestionsAddon, there is no level progression; a single infinite level is used
    const isLevelCompletedAfterStep =
      isFreeFormTastingAddon || isGamifiedSurveyQuestionsAddon
        ? null
        : !levelProgress?.isRewardClaimed &&
          updatedLevelTotal >= levelScoreTarget &&
          !!levelProgress.levelDetails.reward?.slug;

    const prevLevelSlugToHideTooltip = isLevelCompletedAfterStep
      ? (Object.keys(currentGameProgress.levels)[activeLevelOrder - 2] ?? null)
      : null;

    const response: { rewards?: TReward[]; totalScore?: number } = await request({
      body: {
        totalScore: updatedGameTotal,
        ...(isLevelCompletedAfterStep ? { rewards: [levelProgress.levelDetails.reward.slug] } : {}),
      },
      method: REQUEST_METHODS.PATCH,
      url: apiUrlUpdateGameProgress(gameDetails?.slug),
    });

    // For isFreeFormTastingAddon or isGamifiedSurveyQuestionsAddon, there is no steps progression
    const updatedLevelProgress = {
      ...levelProgress,
      doneSteps:
        isFreeFormTastingAddon || isGamifiedSurveyQuestionsAddon
          ? null
          : levelProgress.doneSteps.map((step, i) => (i === activeLevelStepIndex ? true : step)),
      isRewardClaimed: isLevelCompletedAfterStep,
      levelTotalScore: updatedLevelTotal,
      shouldShowAchievementPage: isLevelCompletedAfterStep,
    };

    return {
      activeLevelSlug,
      gameSlug: gameDetails?.slug,
      prevLevelSlugToHideTooltip,
      productInstance,
      updatedLevelProgress,
      updatedRewards: response?.rewards,
      updatedTotalScore: response?.totalScore || updatedGameTotal,
    };
  },
);

export const gamificationSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(actionInitGamification.pending, state => {
        state.isGameInitializing = true;
      })
      .addCase(actionInitGamification.fulfilled, (state, { payload }) => {
        state.isGameInitializing = false;
        if (!payload) return;

        const {
          gameDetails,
          gameEnrollments,
          products,
          usedProducts,
          isFreeFormTastingAddon,
          isGamifiedSurveyQuestionsAddon,
        } = payload;
        const isInfiniteLevelAddon = isFreeFormTastingAddon || isGamifiedSurveyQuestionsAddon;
        const updatedGameDetails = {
          ...gameDetails,
          levels: [...gameDetails.levels].sort((a, b) => a.order - b.order),
        };

        state.gameDetails = updatedGameDetails;
        state.gameEnrollments = gameEnrollments;
        state.products = products;
        state.usedProducts = usedProducts;

        // List of reward slugs that the user has already earned
        const rewardSlugs = (gameEnrollments?.rewards).map(r => r.slug);

        // Initialize or access the progress state for this game
        if (!state.gamesProgress[gameDetails.slug]) {
          state.gamesProgress[gameDetails.slug] = createDefaultGameProgress();
        }
        const levelsProgress = state.gamesProgress[gameDetails.slug].levels;

        gameDetails.levels.forEach(level => {
          const previousLevelProgress = levelsProgress[level.slug];
          const stepsLength = level.steps.length;

          // Case 1: User already earned this reward — mark the level as fully completed
          if (rewardSlugs.includes(level?.reward?.slug)) {
            levelsProgress[level.slug] = {
              doneSteps: Array(stepsLength).fill(true),
              isRewardClaimed: true,
              isRewardValidated: previousLevelProgress?.isRewardValidated,
              levelDetails: level,
              levelTotalScore: level.score ?? 0,
              shouldHideRewardTooltip: previousLevelProgress?.shouldHideRewardTooltip,
              shouldShowAchievementPage: false,
            };
            return;
          }

          // Case 2: Level progress exists — sync it with the updated steps structure
          if (previousLevelProgress) {
            levelsProgress[level.slug] = {
              doneSteps: previousLevelProgress?.doneSteps,
              isRewardClaimed: previousLevelProgress?.isRewardClaimed,
              isRewardValidated: previousLevelProgress?.isRewardValidated,
              levelDetails: level,
              levelTotalScore: previousLevelProgress?.levelTotalScore,
              shouldHideRewardTooltip: previousLevelProgress?.shouldHideRewardTooltip,
              shouldShowAchievementPage: previousLevelProgress?.shouldShowAchievementPage,
            };

            // Case 3: New level — initialize an empty progress entry
          } else {
            // For isFreeFormTastingAddon or isGamifiedSurveyQuestionsAddon, there is no level or step progression.
            // A single infinite level is used, with no Rewards or Achievements.
            levelsProgress[level.slug] = {
              doneSteps: isInfiniteLevelAddon ? null : Array(stepsLength).fill(false),
              isRewardClaimed: isInfiniteLevelAddon ? null : level?.isOnboarding,
              isRewardValidated: isInfiniteLevelAddon ? null : false,
              levelDetails: level,
              levelTotalScore: 0,
              shouldHideRewardTooltip: isInfiniteLevelAddon ? null : level?.isOnboarding,
              shouldShowAchievementPage: isInfiniteLevelAddon ? null : level?.isOnboarding,
            };
          }
        });
      })
      .addCase(actionInitGamification.rejected, state => {
        state.isGameInitializing = false;
      });

    builder
      .addCase(actionUpdateGamificationProgress.pending, state => {
        state.isGameProgressUpdating = true;
      })
      .addCase(actionUpdateGamificationProgress.fulfilled, (state, { payload }) => {
        const {
          gameSlug,
          activeLevelSlug,
          updatedTotalScore,
          updatedRewards,
          productInstance,
          updatedLevelProgress,
          prevLevelSlugToHideTooltip,
        } = payload;
        state.gamesProgress[gameSlug].levels[activeLevelSlug] = updatedLevelProgress;

        if (productInstance) {
          const productIdentifier = productInstance.product.identifier;

          if (!state.gamesProgress[gameSlug].usedProductIds.includes(productIdentifier)) {
            state.gamesProgress[gameSlug].usedProductIds = [
              productIdentifier,
              ...state.gamesProgress[gameSlug].usedProductIds,
            ];
          }

          if (!state.usedProducts.some(product => product.product.identifier === productIdentifier)) {
            state.usedProducts = [productInstance, ...state.usedProducts];
          }
        }

        state.gameEnrollments.totalScore = updatedTotalScore ?? state.gameEnrollments.totalScore;
        state.gameEnrollments.rewards = updatedRewards || state.gameEnrollments.rewards;

        // Hide tooltip for the previous level
        if (prevLevelSlugToHideTooltip) {
          state.gamesProgress[gameSlug].levels[prevLevelSlugToHideTooltip].shouldHideRewardTooltip = true;
        }

        state.isGameProgressUpdating = false;
      })
      .addCase(actionUpdateGamificationProgress.rejected, state => {
        state.isGameProgressUpdating = false;
      });
  },
  initialState,
  name: 'gamification',
  reducers: {
    actionHideAchievementPage(state, action: PayloadAction<{ gameSlug: string; levelSlug: string }>) {
      const { gameSlug, levelSlug } = action.payload;
      const level = state.gamesProgress[gameSlug]?.levels?.[levelSlug];
      if (level) level.shouldShowAchievementPage = false;
    },
    actionHideRewardTooltip(state, action: PayloadAction<{ gameSlug: string; levelSlug: string }>) {
      const { gameSlug, levelSlug } = action.payload;
      const level = state.gamesProgress[gameSlug]?.levels?.[levelSlug];
      if (level) level.shouldHideRewardTooltip = true;
    },
    actionResetGame: () => initialState,
    actionSelectGameProduct(state, action: PayloadAction<TProductInstance>) {
      state.products = [
        action.payload,
        ...state.products.filter(p => p.identifier !== action.payload.product.identifier),
      ];
    },
    actionSetLeaderboardConfirmation(state, action: PayloadAction<{ gameSlug: string; confirmation: boolean }>) {
      const { gameSlug, confirmation } = action.payload;
      state.leaderboardConfirmationByGame[gameSlug] = confirmation;
    },
    actionSetSurveyGame(state, action: PayloadAction<TSurveyGame>) {
      state.surveyGame = action.payload;
    },
    actionValidateReward(state, action: PayloadAction<{ gameSlug: string; levelSlug: string }>) {
      const { gameSlug, levelSlug } = action.payload;
      const level = state.gamesProgress[gameSlug]?.levels?.[levelSlug];
      if (level) level.isRewardValidated = true;
    },
  },
});

export const {
  actionSelectGameProduct,
  actionResetGame,
  actionHideRewardTooltip,
  actionValidateReward,
  actionHideAchievementPage,
  actionSetLeaderboardConfirmation,
  actionSetSurveyGame,
} = gamificationSlice.actions;
export default gamificationSlice.reducer;
